import { isAfter, isBefore } from "date-fns";
import { v4 as uuidv4 } from "uuid";

import { DATE_FORMATS_FNS } from "components";
import { COMMUNICATION_STATUSES, DMS_CAPABILITIES, STATUS_IDENTIFIERS } from "models";
import { formatCurrentUTCDate } from "utils/dateHelperFunctions";

import type { Appointment, Brand, DealerLocation } from "models";

export const FAKE_STATUSES = [
  STATUS_IDENTIFIERS.HandleCheckIn,
  STATUS_IDENTIFIERS.CheckInDone,
  STATUS_IDENTIFIERS.CarInShop,
  STATUS_IDENTIFIERS.CarOkPlusRepairOverivew,
  STATUS_IDENTIFIERS.QualityCheckPlusRepairOverview,
  STATUS_IDENTIFIERS.CarOutOfShop
];

export const updateAppointmentStatusIdentifier = (appointment: Appointment): Appointment => {
  let currentStatus = null;

  const isStatusIn = (statuses: STATUS_IDENTIFIERS[]) => statuses.includes(appointment.appointment_status_identifier);
  const isCurrentTimeBefore = (time: string) => isBefore(new Date(time), new Date());
  const isCurrentTimeAfter = (time: string) => isAfter(new Date(time), new Date());

  if (appointment.check_in_com_to_handle > 0 && isStatusIn([STATUS_IDENTIFIERS.NewCarStatus])) {
    currentStatus = STATUS_IDENTIFIERS.HandleCheckIn;
  } else if (isStatusIn([STATUS_IDENTIFIERS.CarInShop]) && appointment.car_out_of_shop) {
    currentStatus = STATUS_IDENTIFIERS.NewCarStatus;
  } else if (
    isStatusIn([STATUS_IDENTIFIERS.NewCarStatus, STATUS_IDENTIFIERS.CheckInDone]) &&
    appointment.car_in_shop &&
    isCurrentTimeBefore(appointment.car_in_shop) &&
    (!appointment.car_out_of_shop || isCurrentTimeAfter(appointment.car_out_of_shop))
  ) {
    currentStatus = STATUS_IDENTIFIERS.CarInShop;
  } else if (
    isStatusIn([STATUS_IDENTIFIERS.NewCarStatus, STATUS_IDENTIFIERS.HandleCheckIn]) &&
    appointment.check_in_com_answered &&
    appointment.check_in_com_to_handle <= 0
  ) {
    currentStatus = STATUS_IDENTIFIERS.CheckInDone;
  } else if (
    isStatusIn([STATUS_IDENTIFIERS.CarReadyStatus, STATUS_IDENTIFIERS.QualityCheckStatus]) &&
    (appointment.customer_communication?.status === COMMUNICATION_STATUSES.RepairOverviewSent ||
      appointment.customer_communication?.status === COMMUNICATION_STATUSES.RepairOverviewOpened)
  ) {
    currentStatus =
      appointment.appointment_status_identifier === STATUS_IDENTIFIERS.CarReadyStatus
        ? STATUS_IDENTIFIERS.CarOkPlusRepairOverivew
        : STATUS_IDENTIFIERS.QualityCheckPlusRepairOverview;
  }

  return currentStatus ? ({ ...appointment, appointment_status_identifier: currentStatus } as Appointment) : appointment;
};

export const isCarReady = (status: STATUS_IDENTIFIERS | undefined) =>
  status !== undefined && [STATUS_IDENTIFIERS.CarReadyStatus, STATUS_IDENTIFIERS.CarOkPlusRepairOverivew].includes(status);
export const isQualityCheck = (status: STATUS_IDENTIFIERS | undefined) =>
  status !== undefined && [STATUS_IDENTIFIERS.QualityCheckStatus, STATUS_IDENTIFIERS.QualityCheckPlusRepairOverview].includes(status);
export const isWorkNotStarted = (status: STATUS_IDENTIFIERS | undefined) =>
  status !== undefined &&
  [STATUS_IDENTIFIERS.NewCarStatus, STATUS_IDENTIFIERS.HandleCheckIn, STATUS_IDENTIFIERS.CheckInDone, STATUS_IDENTIFIERS.CarInShop].includes(status);
export const isWorkStarted = (status: STATUS_IDENTIFIERS | undefined) => Boolean(status && !isWorkNotStarted(status));
export const isCarReadyOrQualityCheck = (status: STATUS_IDENTIFIERS | undefined) => Boolean(status && (isCarReady(status) || isQualityCheck(status)));
export const isCarReadyOrQualityCheckDoneOrCanceled = (status: STATUS_IDENTIFIERS | undefined) =>
  status !== undefined && (isCarReadyOrQualityCheck(status) || status === STATUS_IDENTIFIERS.CanceledStatus);
export const isAppointmentCanceled = (status: STATUS_IDENTIFIERS) => status === STATUS_IDENTIFIERS.CanceledStatus;

const THREE_DEPARTMENT_VISIBLE_STATUSES = [STATUS_IDENTIFIERS.NewCarStatus, STATUS_IDENTIFIERS.HandleCheckIn, STATUS_IDENTIFIERS.CheckInDone];

export const isThreeDepartmentEditable = (appointment: Appointment) =>
  THREE_DEPARTMENT_VISIBLE_STATUSES.includes(appointment.appointment_status_identifier) && !appointment.car_in_shop;

export const isWarrantyPinSignatureRequiredForVin = (vin: string, location: DealerLocation, brands: Brand[]) => {
  const brandGroups = location.warranty_pin_signature_brand_groups;
  if (!brandGroups?.length) return true;

  return brands.some(brand => brand.vin_prefix && vin.startsWith(brand.vin_prefix) && brandGroups.some(brandGroup => brandGroup.id === brand.brand_group_id));
};

export const isAppointmentWarrantyPinSignatureMissing = (appointment: Appointment, brands: Brand[], location?: DealerLocation | null) => {
  if (!location?.is_warranty_pin_signature_missing_warning_enabled || !appointment.warranty_pin_signature_missing_count) return false;
  return isWarrantyPinSignatureRequiredForVin(appointment.vin_nr, location, brands);
};

export const canWriteBackCustomerData = (location?: DealerLocation | null) => Boolean(location?.dms_capability_ids?.includes(DMS_CAPABILITIES.WriteBackCustomerData));

export const generateDMSNr = () => `claire_${formatCurrentUTCDate(DATE_FORMATS_FNS.ISOFormat)}_${uuidv4()}`;
